import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Badge,
  Divider,
  Flex,
  HStack,
  Heading,
  Input,
  Select,
  Spinner,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { FaChevronLeft, FaChevronRight, FaRegCalendarCheck } from "react-icons/fa";
import { hitBeApi } from "../../../api/api";
import { useAiCreditsGate } from "../../../hooks/useAiCreditsGate";
import { AiCreditsBanner, useAiFeatureColors } from "../ai/aiFeatureUi";

const THEME = "teal";

/** Today's date key (YYYY-MM-DD) in IST — matches the server's day keys. */
function todayKeyIST() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

/** Shift a YYYY-MM-DD key by whole days (date-only math, TZ-agnostic). */
function shiftKey(key, delta) {
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
}

function prettyDate(key) {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

const RANGE_OPTIONS = [
  { value: "last-7-days", label: "Last 7 days" },
  { value: "last-1-month", label: "Last 1 month" },
  { value: "current-month", label: "Current month" },
  { value: "last-1-year", label: "Last 1 year" },
  { value: "custom", label: "Custom range" },
];

function openBuyCredits() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("manage360:ai-credits-exhausted"));
  }
}

export default function DayLifeAi() {
  const toast = useToast();
  const { remaining: aiCreditsRemaining, canUse: canUseAi } = useAiCreditsGate({
    autoPromptOnMount: false,
  });
  const { cardBg, borderColor, muted, accent, panelBg } = useAiFeatureColors(THEME);
  const readOnlyBg = useColorModeValue("gray.100", "gray.700");

  const today = useMemo(() => todayKeyIST(), []);
  const [selectedDate, setSelectedDate] = useState(today);
  const isToday = selectedDate === today;

  // ----- day journal state -----
  const [didWell, setDidWell] = useState("");
  const [couldImprove, setCouldImprove] = useState("");
  const [entryLoading, setEntryLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ----- AI analysis state -----
  const [rangeType, setRangeType] = useState("last-7-days");
  const [customFrom, setCustomFrom] = useState(shiftKey(today, -7));
  const [customTo, setCustomTo] = useState(today);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  // ----- conversation history -----
  const [conversation, setConversation] = useState([]);
  const [convoLoading, setConvoLoading] = useState(true);

  const loadEntry = useCallback(
    async (dateKey) => {
      setEntryLoading(true);
      try {
        const res = await hitBeApi(`daylife/entries/${dateKey}`, "GET");
        const entry = res?.data?.entry || null;
        setDidWell(entry?.didWell || "");
        setCouldImprove(entry?.couldImprove || "");
      } catch (error) {
        toast({
          title: "Could not load that day",
          description: error.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setDidWell("");
        setCouldImprove("");
      } finally {
        setEntryLoading(false);
      }
    },
    [toast]
  );

  const loadConversation = useCallback(async () => {
    setConvoLoading(true);
    try {
      const res = await hitBeApi("daylife/conversation", "GET");
      setConversation(res?.data?.messages || []);
    } catch {
      // history is best-effort
    } finally {
      setConvoLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntry(selectedDate);
  }, [selectedDate, loadEntry]);

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  const goPrevDay = () => setSelectedDate((d) => shiftKey(d, -1));
  const goNextDay = () =>
    setSelectedDate((d) => (d >= today ? d : shiftKey(d, 1)));

  const handleDatePick = (e) => {
    const v = e.target.value;
    if (!v) return;
    if (v > today) {
      toast({
        title: "Future days are locked",
        description: "You can only journal up to today.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setSelectedDate(v);
  };

  const handleSave = async () => {
    if (!isToday) return;
    setSaving(true);
    try {
      await hitBeApi(`daylife/entries/${today}`, "PUT", {
        didWell,
        couldImprove,
      });
      toast({
        title: "Saved today's reflection",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Could not save",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAnalyze = async () => {
    if (!canUseAi) {
      openBuyCredits();
      return;
    }
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const body = { rangeType };
      if (rangeType === "custom") {
        body.from = customFrom;
        body.to = customTo;
      }
      const res = await hitBeApi("daylife/analyze", "POST", body);
      const data = res?.data || {};
      if (data.noEntries) {
        toast({
          title: "Nothing to analyse yet",
          description:
            "There are no journal entries in that range. Write a few days first.",
          status: "info",
          duration: 4000,
          isClosable: true,
        });
        setAnalysis(null);
      } else {
        setAnalysis({ text: data.analysis, range: data.range, count: data.entryCount });
        loadConversation();
      }
    } catch (error) {
      toast({
        title: "Analysis failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <VStack align="stretch" spacing={5} w="100%" maxW="900px" mx="auto" pb={6}>
      <AiCreditsBanner
        canUse={canUseAi}
        remaining={aiCreditsRemaining}
        onBuy={openBuyCredits}
      />

      {/* ---------- Daily journal ---------- */}
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        boxShadow="sm"
        p={{ base: 4, md: 5 }}
      >
        <Flex
          justify="space-between"
          align="center"
          mb={4}
          gap={3}
          flexWrap="wrap"
        >
          <Heading size="md">Daily reflection</Heading>
          <HStack spacing={2}>
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<FaChevronLeft />}
              onClick={goPrevDay}
              aria-label="Previous day"
            >
              Prev
            </Button>
            <Input
              type="date"
              size="sm"
              value={selectedDate}
              max={today}
              onChange={handleDatePick}
              w="auto"
            />
            <Button
              size="sm"
              variant="ghost"
              rightIcon={<FaChevronRight />}
              onClick={goNextDay}
              isDisabled={isToday}
              aria-label="Next day"
            >
              Next
            </Button>
            {!isToday ? (
              <Button
                size="sm"
                colorScheme={accent}
                variant="outline"
                leftIcon={<FaRegCalendarCheck />}
                onClick={() => setSelectedDate(today)}
              >
                Today
              </Button>
            ) : null}
          </HStack>
        </Flex>

        <HStack mb={3} spacing={2}>
          <Text fontWeight="semibold">{prettyDate(selectedDate)}</Text>
          {isToday ? (
            <Badge colorScheme={accent}>Editable</Badge>
          ) : (
            <Badge colorScheme="gray">Read-only</Badge>
          )}
        </HStack>

        {entryLoading ? (
          <Flex justify="center" py={8}>
            <Spinner color={`${accent}.500`} />
          </Flex>
        ) : (
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                What I did well today
              </Text>
              <Textarea
                value={didWell}
                onChange={(e) => setDidWell(e.target.value)}
                isReadOnly={!isToday}
                bg={isToday ? undefined : readOnlyBg}
                placeholder={
                  isToday
                    ? "Wins, good habits, things you're proud of…"
                    : "Nothing recorded for this day."
                }
                minH="120px"
                resize="vertical"
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                What I could have done better
              </Text>
              <Textarea
                value={couldImprove}
                onChange={(e) => setCouldImprove(e.target.value)}
                isReadOnly={!isToday}
                bg={isToday ? undefined : readOnlyBg}
                placeholder={
                  isToday
                    ? "Slip-ups, time wasted, things to improve tomorrow…"
                    : "Nothing recorded for this day."
                }
                minH="120px"
                resize="vertical"
              />
            </Box>
            {isToday ? (
              <Flex justify="flex-end">
                <Button
                  colorScheme={accent}
                  onClick={handleSave}
                  isLoading={saving}
                  loadingText="Saving"
                >
                  Save today
                </Button>
              </Flex>
            ) : (
              <Text fontSize="xs" color={muted}>
                Past days are locked — open today to write or edit.
              </Text>
            )}
          </VStack>
        )}
      </Box>

      {/* ---------- AI analysis ---------- */}
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        boxShadow="sm"
        p={{ base: 4, md: 5 }}
      >
        <Heading size="md" mb={1}>
          Ask DayLife AI
        </Heading>
        <Text fontSize="sm" color={muted} mb={4}>
          Analyse your reflections over a period and get specific, kind advice on
          what to improve tomorrow and how to grow steadily.
        </Text>

        <Flex gap={3} align="flex-end" flexWrap="wrap" mb={4}>
          <Box>
            <Text fontSize="xs" fontWeight="semibold" mb={1} color={muted}>
              Period
            </Text>
            <Select
              size="sm"
              value={rangeType}
              onChange={(e) => setRangeType(e.target.value)}
              w="auto"
              minW="160px"
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Box>
          {rangeType === "custom" ? (
            <>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" mb={1} color={muted}>
                  From
                </Text>
                <Input
                  type="date"
                  size="sm"
                  value={customFrom}
                  max={today}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  w="auto"
                />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" mb={1} color={muted}>
                  To
                </Text>
                <Input
                  type="date"
                  size="sm"
                  value={customTo}
                  max={today}
                  onChange={(e) => setCustomTo(e.target.value)}
                  w="auto"
                />
              </Box>
            </>
          ) : null}
          <Button
            colorScheme={accent}
            onClick={handleAnalyze}
            isLoading={analyzing}
            loadingText="Analysing"
          >
            Analyse
          </Button>
        </Flex>

        {analysis ? (
          <Box
            bg={panelBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            p={4}
          >
            <HStack mb={2} spacing={2}>
              <Badge colorScheme={accent}>{analysis.range?.label}</Badge>
              <Text fontSize="xs" color={muted}>
                {analysis.count} {analysis.count === 1 ? "day" : "days"} analysed
              </Text>
            </HStack>
            <Text whiteSpace="pre-wrap" lineHeight="tall">
              {analysis.text}
            </Text>
          </Box>
        ) : null}
      </Box>

      {/* ---------- Conversation history ---------- */}
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        boxShadow="sm"
        p={{ base: 4, md: 5 }}
      >
        <Heading size="sm" mb={3}>
          Past coaching conversations
        </Heading>
        {convoLoading ? (
          <Flex justify="center" py={6}>
            <Spinner color={`${accent}.500`} />
          </Flex>
        ) : conversation.length === 0 ? (
          <Text fontSize="sm" color={muted}>
            Your AI analyses will appear here and feed future conversations so the
            advice stays consistent over time.
          </Text>
        ) : (
          <VStack align="stretch" spacing={3} divider={<Divider />}>
            {conversation.map((m) => (
              <Box key={m._id}>
                <HStack mb={1} spacing={2}>
                  <Badge
                    colorScheme={m.role === "assistant" ? accent : "gray"}
                  >
                    {m.role === "assistant" ? "DayLife AI" : "You"}
                  </Badge>
                  {m.createdAt ? (
                    <Text fontSize="xs" color={muted}>
                      {new Date(m.createdAt).toLocaleString("en-GB")}
                    </Text>
                  ) : null}
                </HStack>
                <Text
                  fontSize="sm"
                  whiteSpace="pre-wrap"
                  lineHeight="tall"
                  noOfLines={m.role === "assistant" ? 8 : 2}
                >
                  {m.content}
                </Text>
              </Box>
            ))}
          </VStack>
        )}
      </Box>
    </VStack>
  );
}
